<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import VoiceNumbersAPI from 'dashboard/api/voiceNumbers';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  voiceNumberId: { type: Number, required: true },
  configured: { type: Boolean, default: false },
});

const { t } = useI18n();

const health = ref(null);
const isChecking = ref(false);
const isProvisioning = ref(false);

// A number carries one inbound webhook, so the only question that matters is whether it is ours -
// unless inbound is off here, and then the line is meant to ring elsewhere.
const statusMessage = computed(() => {
  if (!health.value) return '';
  if (!health.value.number_found) {
    return t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.NOT_FOUND');
  }
  if (health.value.points_here) {
    return t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.POINTS_HERE');
  }
  return health.value.inbound_enabled
    ? t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.POINTS_ELSEWHERE')
    : t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.INBOUND_OFF');
});

// The TwiML app is what the agents' browsers dial into, and it is the half that can be ours while
// the line itself keeps ringing in another installation.
const clientMessage = computed(() =>
  health.value?.client_points_here
    ? t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.CLIENT_POINTS_HERE')
    : t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.CLIENT_POINTS_ELSEWHERE')
);

const inboundSettled = computed(
  () =>
    !!health.value &&
    (health.value.points_here || !health.value.inbound_enabled)
);

// Nothing left to take: the browser calls are ours and the line is where this install wants it.
const isUpToDate = computed(
  () => inboundSettled.value && !!health.value?.client_points_here
);

const lastProvisionedLabel = computed(() =>
  health.value?.provisioned_at
    ? new Date(health.value.provisioned_at).toLocaleString()
    : t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.NEVER_PROVISIONED')
);

const check = async () => {
  isChecking.value = true;
  try {
    const { data } = await VoiceNumbersAPI.health(props.voiceNumberId);
    health.value = data;
  } catch (error) {
    useAlert(t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.CHECK_ERROR'));
  } finally {
    isChecking.value = false;
  }
};

const provision = async () => {
  isProvisioning.value = true;
  try {
    const { data } = await VoiceNumbersAPI.provision(props.voiceNumberId);
    health.value = data;
    useAlert(
      t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.PROVISION_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.PROVISION_ERROR')
    );
  } finally {
    isProvisioning.value = false;
  }
};

onMounted(check);
</script>

<template>
  <SettingsFieldSection
    :label="$t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.LABEL')"
    :help-text="$t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.DESCRIPTION')"
  >
    <div class="flex flex-col gap-3">
      <div v-if="isChecking && !health" class="flex items-center gap-2">
        <Spinner class="size-4 text-n-slate-11" />
      </div>

      <template v-else-if="health">
        <div class="flex items-start gap-2">
          <span
            class="mt-1.5 rounded-full size-2 shrink-0"
            :class="inboundSettled ? 'bg-n-teal-9' : 'bg-n-amber-9'"
          />
          <span class="text-sm text-n-slate-12">{{ statusMessage }}</span>
        </div>

        <div v-if="health.client_call_url" class="flex items-start gap-2">
          <span
            class="mt-1.5 rounded-full size-2 shrink-0"
            :class="health.client_points_here ? 'bg-n-teal-9' : 'bg-n-amber-9'"
          />
          <span class="text-sm text-n-slate-12">{{ clientMessage }}</span>
        </div>

        <div class="flex flex-col gap-1 text-xs text-n-slate-11">
          <span v-if="health.provider_call_url" class="break-all">
            {{ $t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.CURRENT_URL') }}
            {{ health.provider_call_url }}
          </span>
          <span
            v-if="health.client_call_url && !health.client_points_here"
            class="break-all"
          >
            {{ $t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.CLIENT_URL') }}
            {{ health.client_call_url }}
          </span>
          <span v-if="!isUpToDate" class="break-all">
            {{ $t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.EXPECTED_URL') }}
            {{ health.expected_call_url }}
          </span>
          <span>
            {{
              $t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.LAST_PROVISIONED')
            }}
            {{ lastProvisionedLabel }}
          </span>
        </div>
      </template>

      <p v-if="!configured" class="text-sm text-n-amber-11">
        {{ $t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.NOT_CONFIGURED') }}
      </p>

      <div class="flex items-center gap-2">
        <NextButton
          faded
          :label="$t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.CHECK')"
          :is-loading="isChecking"
          @click="check"
        />
        <NextButton
          :label="$t('INBOX_MGMT.VOICE_CONFIGURATION.PROVISIONING.PROVISION')"
          :disabled="!configured || isUpToDate"
          :is-loading="isProvisioning"
          @click="provision"
        />
      </div>
    </div>
  </SettingsFieldSection>
</template>
